import { classNames } from "~/utils";
import { motion } from "framer-motion";
import React from "react";
import { BulbIcon } from "~/components/icons/bulb-icon";

interface QuestionMCQProps {
  question?: {
    id: string;
    question: string;
    answers: {
      id: string;
      answer: string;
    }[];
  };
  triviaSelected: (ans: string) => void;
  selectedAns: string;
  onInitialOrderSet: (answerIds: string[]) => void;
}

export function QuestionMCQ({
  question,
  triviaSelected,
  selectedAns,
  onInitialOrderSet,
  children,
}: React.PropsWithChildren<QuestionMCQProps>) {
  // As the backend is shuffling the answers on the question, store the initial order so we can preserve it when the result is shown
  React.useEffect(() => {
    if (question?.answers) {
      const initialOrder = question.answers.map((a) => a.id);
      onInitialOrderSet(initialOrder);
    }
  }, [question?.answers, onInitialOrderSet]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.4 } }}
      exit={{ opacity: 0 }}
      className="flex flex-1 flex-col space-y-5 justify-between z-10 overflow-x-hidden"
    >
      <div className="flex flex-1 flex-col space-y-5 justify-center items-center relative relative overflow-x-hidden overflow-y-auto">
        <div className="flex flex-col flex-none items-center space-y-6 min-h-0">
          <div className="h-12 w-12 rounded-xl bg-secondary flex items-center justify-center z-10">
            <BulbIcon className="text-primary" />
          </div>
          <div className="text-center md:text-left text-lg font-semibold leading-7 z-10">
            {question?.question || ""}
          </div>
        </div>
        <div className="w-full flex flex-col flex-1 sm:flex-none items-center space-y-6 z-1 ">
          <div className="flex flex-col items-center w-full space-y-2">
            {question?.answers.map((answer) => (
              <div key={answer.id} className="w-full p-1">
                <div
                  className={classNames(
                    "flex w-full items-center space-x-1 p-4 text-sm text-muted-foreground font-medium rounded-lg min-h-[55px] cursor-pointer",
                    "transition-all duration-200 ease-in-out",
                    "border hover:border-primary",
                    selectedAns === answer.id
                      ? "border-primary shadow-[0_0_0_1px_theme(colors.primary)]"
                      : "border",
                  )}
                  onClick={() => triviaSelected(answer.id)}
                >
                  {answer.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {children || null}
    </motion.div>
  );
}
